import type React from 'react'
import { useEffect, useState } from 'react'
import styled from 'styled-components'

import CommentsLabel from '@/components/CommentsLabel'
import CommentsSidebar from '@/components/CommentsSidebar'
import { ActionButtonContainer } from '@/components/DecisionPanelCards/Shared'
import {
  displayFailReasons,
  displaySuccessReasons,
} from '@/components/DecisionPanelCards/utils'
import DocumentRequests from '@/components/DocumentRequests'
import { isNonProfitOrganisation } from '@/components/EntityPanels/OrganisationalDetails/utils'
import ExpandableCard from '@/components/ExpandableCard'
import { ButtonsContainer } from '@/components/IdentityCard/IdentityCard'
import OutcomeStatusPill from '@/components/OutcomeStatusPill'
import ProsCons from '@/components/ProsCons/ProsCons'
import VerifyOrganisationCredit from '@/components/VerifyOrganisationCredit/index'
import VerifyOrganisationFinance from '@/components/VerifyOrganisationFinance'
import { OutcomeSectionName } from '@/lib/apollo/model/outcome'
import { SectionName } from '@/lib/apollo/query/get-document-requests'
import type { Application } from '@/types/Activity'
import type { Touchpoint } from '@/types/Touchpoints'
import { isCorporateTenant, isRefCompleted } from '@/utils/subjectHelpers'

import ForProfitOrganisationDetails from './ForProfitOrganisationDetails'
import NotForProfitOrganisationDetails from './NotForProfitOrganisationDetails'

interface Props {
  application: Application
  subjectTouchpoints: Touchpoint[]
  isLoadingTouchpoints: boolean
  fetchSubjectTouchpoints: () => Promise<void>
}

const Body = styled.div`
  margin-top: 20px;
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 20px;
  transition: height 0.25s ease-in;
`

const OrganisationFinanceCard: React.FC<Props> = ({
  application,
  fetchSubjectTouchpoints,
  isLoadingTouchpoints,
  subjectTouchpoints,
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)
  const [commentsSidebarOpen, setCommentsSidebarOpen] = useState<boolean>(false)
  const [financialSuccess, setFinancialSuccess] = useState<string[]>([])
  const [financialFail, setFinancialFail] = useState<string[]>([])

  const corporateLetTenantSubject = isCorporateTenant(
    application.type,
    application?.context?.journeyType,
  )

  const isNonProfit =
    application.context?.company?.verification?.companyType !== undefined &&
    isNonProfitOrganisation(application.context?.company?.verification?.companyType)

  const outcomeSections = application?.outcome?.sections ?? []
  const financialOutcome = outcomeSections.find(
    outcomeSection => outcomeSection.name === OutcomeSectionName.financial,
  )
  const overallOutcomeStatus = application.outcome?.status
  const financeOutcomeStatus = financialOutcome?.status

  const sectionComments = subjectTouchpoints.filter(
    ({ category }) => category.toLowerCase() === 'financial',
  )

  useEffect(() => {
    const propertyName = isNonProfit ? 'organisationFinancial' : 'organisationCredit'

    setFinancialSuccess(
      displaySuccessReasons(application.results ?? [], 'financial', propertyName),
    )
    setFinancialFail(
      displayFailReasons(application.results ?? [], 'financial', propertyName),
    )
  }, [application, isNonProfit])

  return (
    <>
      <CommentsSidebar
        disableLeaveComment={true}
        isOpen={commentsSidebarOpen}
        application={application}
        onClose={() => setCommentsSidebarOpen(false)}
        touchpoints={sectionComments}
        activeEntity="FINANCIAL"
        fetchSubjectTouchpoints={fetchSubjectTouchpoints}
      />
      <ExpandableCard
        isExpanded={isExpanded}
        handleExpand={() => setIsExpanded(!isExpanded)}
        label="Financial"
        icon="income"
        actionButton={
          <ActionButtonContainer>
            <div>
              <CommentsLabel
                isLoadingTouchpoints={isLoadingTouchpoints}
                onToggle={() => setCommentsSidebarOpen(!commentsSidebarOpen)}
                sectionComments={sectionComments}
              />
            </div>

            {overallOutcomeStatus && financeOutcomeStatus && (
              <OutcomeStatusPill
                overallOutcomeStatus={overallOutcomeStatus}
                outcomeStatus={financeOutcomeStatus}
              />
            )}
          </ActionButtonContainer>
        }
        expandedContent={
          <Body>
            <ButtonsContainer>
              <DocumentRequests
                customerId={application.customerId}
                applicationId={application.applicationId}
                subjectId={application.subjectId}
                sectionName={SectionName.financial}
                subjectName={`${application.context?.firstName} ${application.context?.lastName}`}
                isRefCompleted={isRefCompleted(application)}
                isCorporateTenantSubject={corporateLetTenantSubject}
              />
              {application.context?.company !== undefined && !isNonProfit && (
                <VerifyOrganisationCredit
                  customerId={application.customerId}
                  subjectId={application.subjectId}
                  company={application.context?.company}
                />
              )}
              {application.context?.company !== undefined && isNonProfit && (
                <VerifyOrganisationFinance
                  customerId={application.customerId}
                  subjectId={application.subjectId}
                  company={application.context?.company}
                />
              )}
            </ButtonsContainer>
            <ProsCons pros={financialSuccess} cons={financialFail} />

            {isNonProfit === true ? (
              <NotForProfitOrganisationDetails application={application} />
            ) : (
              <ForProfitOrganisationDetails application={application} />
            )}
          </Body>
        }
      />
    </>
  )
}

export default OrganisationFinanceCard
